/**
 * PDF import helpers (ER-12394 — Article Checkpoint import from PDF).
 *
 * Mirrors `scribeImport.js`: client-side latency checks for `PdfImportDialog` and the
 * backend error-code → user-facing message mapping. The service layer (ER-12393)
 * re-validates everything; the client checks only avoid a wasted upload.
 */

/** Spec Decision 2: 25 MB limit, no page-count limit. */
export const PDF_IMPORT_MAX_BYTES = 25 * 1024 * 1024;

/** Backend error codes (Rules 2 and 3 of the spec page). */
export const PDF_IMPORT_ERROR_CODES = {
  INVALID_FILE: "PDF_IMPORT_INVALID_FILE",
  ENCRYPTED: "PDF_IMPORT_ENCRYPTED",
  FILE_TOO_LARGE: "PDF_IMPORT_FILE_TOO_LARGE",
  NO_TEXT: "PDF_IMPORT_NO_TEXT",
};

/** Distinct user-facing copy for each backend error code. */
export const PDF_IMPORT_ERROR_MESSAGES = {
  [PDF_IMPORT_ERROR_CODES.INVALID_FILE]: "That file isn't a valid PDF.",
  [PDF_IMPORT_ERROR_CODES.ENCRYPTED]:
    "This PDF is password-protected. Remove the password and try again.",
  [PDF_IMPORT_ERROR_CODES.FILE_TOO_LARGE]: "This PDF is larger than 25 MB.",
  [PDF_IMPORT_ERROR_CODES.NO_TEXT]:
    "This PDF has no selectable text (it may be a scan). Scanned PDFs can't be imported.",
};

/** Fallback for an unknown code, a job failure or a timeout. */
export const DEFAULT_PDF_IMPORT_ERROR_MESSAGE =
  "We couldn't import this PDF. Try again, or contact support if it keeps failing.";

/** Picker-state copy when more than one file is dropped or chosen. */
export const PDF_IMPORT_MULTIPLE_FILES_MESSAGE = "Select one PDF at a time.";

/**
 * Client-side latency check (Rule 2): is this file a PDF within the size limit?
 *
 * @param {File} file the dropped / chosen file
 * @returns {string} the error code to show, or "" when the file is acceptable
 */
export function validatePdfFile(file) {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  if (type !== "application/pdf" && !name.endsWith(".pdf")) {
    return PDF_IMPORT_ERROR_CODES.INVALID_FILE;
  }
  if (Number(file?.size) > PDF_IMPORT_MAX_BYTES) {
    return PDF_IMPORT_ERROR_CODES.FILE_TOO_LARGE;
  }
  return "";
}

/**
 * Resolve a backend error code to its user-facing message.
 *
 * @param {string} code backend error code
 * @param {string} [fallback] message used when the code is unknown
 * @returns {string}
 */
export function getPdfImportErrorMessage(code, fallback = DEFAULT_PDF_IMPORT_ERROR_MESSAGE) {
  return PDF_IMPORT_ERROR_MESSAGES[code] || fallback;
}

export function codeFromPayload(data) {
  if (!data || typeof data !== "object") {
    return "";
  }
  for (const field of ["code", "error_code", "error", "detail"]) {
    const value = data[field];
    if (typeof value === "string" && value) {
      return value;
    }
    if (value && typeof value === "object" && typeof value.code === "string" && value.code) {
      return value.code;
    }
  }
  return "";
}

/**
 * Pull the error code out of an axios-style error (`err.response.data`).
 *
 * @param {*} error axios error (or anything thrown)
 * @returns {string} the error code, or "" when none is present
 */
export function resolvePdfImportErrorCode(error) {
  return codeFromPayload(error?.response?.data);
}

/**
 * User-facing message for a rejected start request (422).
 *
 * @param {*} error axios error
 * @returns {string}
 */
export function getPdfImportStartErrorMessage(error) {
  return getPdfImportErrorMessage(resolvePdfImportErrorCode(error));
}

/**
 * User-facing message for a job that ended `failed`; the status payload carries the
 * code when it is a known one (e.g. PDF_IMPORT_NO_TEXT), otherwise the generic fallback.
 *
 * @param {object|null} data the failed status payload
 * @returns {string}
 */
export function getPdfImportJobFailureMessage(data) {
  return getPdfImportErrorMessage(codeFromPayload(data));
}

/**
 * Human-readable size for the selected-file card, e.g. "2.4 MB".
 *
 * @param {number} bytes
 * @returns {string}
 */
export function formatPdfFileSize(bytes) {
  const value = Number(bytes) || 0;
  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (value >= 1024) {
    return `${Math.round(value / 1024)} KB`;
  }
  return `${value} B`;
}

export function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * Rule 8 import summary shown once the editor is populated.
 *
 * "Imported {block_count} blocks and {image_count} images." plus, only when something was
 * left out, " Not carried over: …" naming images that became placeholders and, when
 * `skipped_graphics > 0`, charts and drawings.
 *
 * @param {object} summary `result.summary` of a succeeded PDF import
 * @returns {string} the toast text, or "" when the result carries no summary
 */
export function buildPdfImportSummaryMessage(summary) {
  if (!summary || typeof summary !== "object") {
    return "";
  }
  const failedImages = Array.isArray(summary.failed_images) ? summary.failed_images.length : 0;
  const notCarriedOver = [];
  if (failedImages > 0) {
    notCarriedOver.push(
      `${failedImages} image(s) that couldn't be imported (replaced with placeholders)`
    );
  }
  if (toCount(summary.skipped_graphics) > 0) {
    notCarriedOver.push("charts and drawings");
  }
  const blocks = toCount(summary.block_count);
  const images = toCount(summary.image_count);
  const imported = `Imported ${blocks} blocks and ${images} images.`;
  if (!notCarriedOver.length) {
    return imported;
  }
  return `${imported} Not carried over: ${notCarriedOver.join("; ")}.`;
}
